import * as React from 'react';

import type { HeadProps, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { ButtonLink } from 'stile-shared/src/components/2020/common/ButtonLink';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import {
  H2,
  H3,
  Lead,
  P,
  PageHeading,
  Spacer,
  Text,
  Ul,
} from 'stile-shared/src/components/2020/common/Primitives';
import { Column } from 'stile-shared/src/components/2020/layout/Column';
import { Icon } from 'stile-shared/src/components/2020/layout/Icon';
import { Row } from 'stile-shared/src/components/2020/layout/Row';
import { CONTACT, FACEBOOK_URL, INSTAGRAM_URL, TWITTER_URL } from 'stile-shared/src/constants';
import { theme } from 'stile-shared/src/styles/2020/theme';

import { PageLayout } from 'templates/2020/PageLayout';
import { SEO } from 'templates/2020/SEO';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="The Sylla-bus is swinging by your school"
      description="Preparing you for the new science syllabus with coffee, incursions and networking (drinks
          on us!)"
      slug={props.location.pathname}
    />
  );
}

function ResourcesPage(props: PageProps) {
  const roadshowItems = [
    {
      title: 'Sydney',
      times: 'Tuesday, 23rd July - Friday, 26th July',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Central Coast/Newcastle',
      locations: 'Gosford, Erina, Tuggerah Lakes, Lake Macquarie, Newcastle',
      times: 'Monday, 29th July - Friday, 2nd August',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Lower - Mid North Coast',
      locations: 'Kempsey, Taree, Forster, Port Macquarie, Coffs Harbour',
      times: 'Monday, 5th August - Friday, 9th August',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Northern Rivers',
      locations: 'Grafton, Ballina, Tweed Heads, Kingscliff',
      times: 'Monday, 12th August - Friday, 16th August',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Northern Tablelands',
      locations: 'Lismore/Grafton/Casino, Inverell, Armidale and Tamworth/Gunnedah',
      times: 'Monday, 19th August - Friday, 23rd August',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Bowral to Albury',
      locations: 'Bowral, Goulburn, Queanbeyan, Albury',
      times: 'Monday, 2nd September - Friday, 6th September',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Riverina/Shoalhaven',
      locations: 'Wagga Wagga, Tumut, Bega, Nowra,  Wollongong, LAZSTA Conference',
      times: 'Monday, 9th September - Friday, 13th September',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Central West',
      locations: 'Katoomba, Bathurst, Orange and Forbes/Parkes',
      times: 'Monday, 16th September - Friday, 20th September',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
    {
      title: 'Central West/Hunter',
      locations: 'Dubbo, Mudgee, Mussellbrook and Hunter Region / Newcastle',
      times: 'Monday, 23rd September - Friday, 27th September',
      url: 'https://forms.gle/nhMVmb76BUx6WRzB6',
    },
  ];

  const social = [
    {
      text: 'Facebook',
      iconId: 'facebook',
      url: FACEBOOK_URL,
    },
    {
      text: 'Instagram',
      iconId: 'instagram',
      url: INSTAGRAM_URL,
    },
    {
      text: 'Twitter',
      iconId: 'twitter',
      url: TWITTER_URL,
    },
  ];

  const roadshowEvents = [
    {
      title: 'Lake Macquarie & Newcastle',
      subtitle: '30th July - Science Teacher Meetup',
      link: 'https://www.eventbrite.com.au/e/954580775797?aff=oddtdtcreator',
      date: '4:00 PM – 10:00 PM (PDT)',
      location: 'Argenton Hotel',
    },
    {
      title: 'Central Coast',
      subtitle: '1st August - Science Teacher Meetup',
      link: 'https://www.eventbrite.com.au/e/954583062637?aff=oddtdtcreator',
      date: '5:00 PM – 6:00 PM (AEST)',
      location: 'Drifters Wharf',
    },
    {
      title: 'Port Macquarie',
      subtitle: '6th August - Science Teacher Meetup',
      link: 'https://www.eventbrite.com.au/e/952462219137?aff=oddtdtcreator',
      date: '5:00 PM – 6:00 PM (AEST)',
      location: 'Zebu Bar & Restaurant',
    },
    {
      title: 'Coffs Harbour',
      subtitle: '8th August - Science Teacher Meetup',
      link: 'https://www.eventbrite.com.au/e/nsw-science-teacher-meetup-coffs-harbour-tickets-971603912457',
      date: '5:00 PM – 6:00 PM (AEST)',
      location: 'Element Bar',
    },
    {
      title: 'Ballina',
      subtitle: '13th August - Science Teacher Meetup',
      link: 'https://www.eventbrite.com.au/e/ballina-science-teacher-meetup-tickets-974202033507',
      date: '5:00 PM – 6:00 PM (AEST)',
      location: 'Australian Hotel Ballina',
    },
    {
      title: 'Lismore',
      subtitle: '15th August - Science Teacher Meetup',
      link: 'https://www.eventbrite.com.au/e/lismore-science-teacher-meetup-tickets-971833158137',
      date: '5:00 PM – 6:00 PM (AEST)',
      location: 'The Sherwood Hotel',
    },
  ];

  return (
    <PageLayout {...props}>
      <TextBlock>
        <PageHeading>
          The Sylla-<em>bus</em> is swinging by your school
        </PageHeading>
        <Lead as="h2">
          Preparing you for the new science syllabus with coffee, incursions and networking (drinks
          on us!)
        </Lead>
        <StaticImage src="../../../stile-shared/assets/images/nsw/roadshow.png" alt="" />
        <P>
          That’s right, you read correctly, Stile is hitting the road in Term 3 in a mean green
          coffee-making machine. We’re coming to a school near you; helping you prepare for the new
          science syllabus while making coffee, hosting pub trivia, running escape room incursions
          and training sessions. It’s going to be an absolute blast!
        </P>
        <P>
          Stile is the complete program for the new science syllabus. Customised for your school. We
          want to help every secondary school across NSW feel ready and confident to transition to
          the new science syllabus with Stile by their side.
        </P>
        <P>
          If you’re keen to join in the science-y fun, book in the Sylla-<em>bus</em> for your
          school!
        </P>
      </TextBlock>

      <TextBlock variant="dark">
        <H2>
          The Stile Sylla-<em>bus</em> Experience
        </H2>
        <P>We have four exciting experiences on offer (all free of charge, of course!):</P>
        <Ul>
          <li>
            <strong>Science escape room</strong>
            <br />A Stile-ish teacher will run a hands-on escape room, complete with materials,
            prizes, and fun for your students.
          </li>
          <li>
            <strong>Staff training session</strong>
            <br />
            The Stile Team will share the key changes to the science syllabus and how Stile can
            help.
          </li>
          <li>
            <strong>Demonstration lesson</strong>
            <br />A qualified science teacher will teach a Stile lesson of your choosing with your
            students.
          </li>
          <li>
            <strong>Personalised meeting</strong>
            <br />
            The Stile Team will discuss how to implement the new science syllabus with Stile.
          </li>
        </Ul>
        <P>
          We’ve designed each experience to run for a period (roughly 45 minutes), but can tailor
          them to suit your needs. In addition, we’ll host coffee and put on networking drinks and
          pub trivia (drinks on us!).
        </P>
        <P>
          If you’re keen to join in the science-y fun, book in the Sylla-<em>bus</em> for your
          school!
        </P>
      </TextBlock>

      <TextBlock stretch>
        <H2>
          Book a visit from the Sylla-<em>bus</em>
        </H2>
        {roadshowItems.map((item) => (
          <RoadshowItem
            title={item.title}
            key={item.title + item.locations}
            locations={item.locations}
            times={item.times}
            url={item.url}
          />
        ))}

        <Spacer />

        <H3>Sydney schools</H3>
        <P>
          Sydney, we’re kicking off the Stile Sylla-<em>bus</em> tour with you in Week 1. If this
          week doesn’t suit, there may be opportunities for us to visit you later on. We also have a
          dedicated team based in the north and south year-round. Fill out the{' '}
          <Link openInNewTab url="https://forms.gle/nhMVmb76BUx6WRzB6">
            Stile Sylla-<em>bus</em> Experience form
          </Link>{' '}
          and include dates that suit you and we’ll do our very best to accommodate you.
        </P>

        <Spacer />

        <H3>Outside of the roadshow regions?</H3>
        <P>
          If you can’t find your region on the roadshow, don’t stress! We’ll try our best to
          accommodate you.
        </P>
        <P>
          Fill in{' '}
          <Link openInNewTab url="https://forms.gle/nhMVmb76BUx6WRzB6">
            this form
          </Link>{' '}
          selecting the ‘Other’ option and we’ll be in touch with more details.
        </P>
      </TextBlock>

      <TextBlock stretch>
        <H2 id="UpcomingRoadshowMeetups">Upcoming Roadshow Meetups</H2>
        {roadshowEvents.map((item) => (
          <RoadshowMeetup key={item.title} {...item} />
        ))}
      </TextBlock>

      <TextBlock title="Frequently Asked Questions (FAQs)" variant="beige" space={theme.space.s}>
        <H3>How much does each experience cost?</H3>
        <P>Each experience is delivered by the Stile team free of charge!</P>
        <Spacer />
        <H3>We don’t use Stile. Can we still participate?</H3>
        <P>
          Yes! We’re happy to run any of the experiences at both schools with and without Stile
          subscriptions. We want to help every secondary school across NSW feel ready and confident
          to transition to the new science syllabus with Stile by their side.
        </P>
        <Spacer />
        <H3>
          My friend/family member at a school wants the Stile Sylla-<em>bus</em> to visit them. How
          can I organise this?
        </H3>
        <P>
          Ask them to fill out{' '}
          <Link openInNewTab url="https://forms.gle/nhMVmb76BUx6WRzB6">
            this form
          </Link>
          , and we’ll do our very best to fit them in!
        </P>
        <Spacer />
        <H3>
          What information do I need to provide in the lead-up to our Stile Sylla-<em>bus</em>{' '}
          visit?
        </H3>
        <P>We need to know the following:</P>
        <Ul>
          <li>The number of staff or students attending the experience</li>
          <li>Information for a key contact person, including name and mobile number</li>
          <li>Any parking and gate information relevant to your school</li>
        </Ul>
        <P>
          We also ask that you notify the school receptionist and any other relevant people that
          we’ll be coming.
        </P>
        <P>
          These questions are asked by the Roadshow Team in your confirmation email. If you haven’t
          sent us this information yet, please email{' '}
          <Link url="mailto:nswroadshow@stileeducation.com">nswroadshow@stileeducation.com</Link>.
        </P>
        <Spacer />
        <H3>Can we have multiple experiences running at our school in a day?</H3>
        <P>
          We’re aiming to visit as many schools as possible. If time allows, we can deliver multiple
          experiences at your school, but we may also need to leave to visit another school.
        </P>
        <Spacer />
        <H3>What do we need to provide?</H3>
        <P>
          We’ll supply all of the materials needed for each experience, but we will also require:
        </P>
        <Ul>
          <li>Access to an enclosed, classroom-type space to host the experience of your choice</li>
          <li>
            A projector or screen that we can project a laptop onto. We’ll bring the necessary
            adaptors to make this as easy as possible
          </li>
          <li>
            Completed release forms for any students participating. We’ll send the form to you ahead
            of time so students can have it signed by a parent or guardian and bring it back to
            school before our visit.{' '}
            <strong>
              We won’t take footage of students who haven’t returned a signed release form
            </strong>
            .
          </li>
        </Ul>
        <Spacer />
        <H3>What do you need for the coffee van experience?</H3>
        <P>
          We’re hoping to deliver coffee before or after your chosen experience as often as
          possible, depending on whether we need to run off to another school.
        </P>
        <P>
          If we can host coffee at your school, we’ll set up in your school’s car park, and staff
          and parents will be invited to come say ‘hi’ and order their coffee, tea, or hot
          chocolate. The Stile Sylla-<em>bus</em> is fitted out with a coffee machine, complete with
          different types of milk, sugar, spoons—all the classics! We encourage you to bring your
          own mug or reusable cup to help minimise our impact on the planet.
        </P>
        <Spacer />
        <H3>What will happen on the day?</H3>
        <P>
          We’ll aim to get to your school 30-45 minutes before the experience (earlier if we’re
          providing coffee, but again, this is dependent on our other commitments!). We’ll sign in
          to reception, meet your chosen representative, and begin setting up in the room. For set
          up, <strong>we’ll need access to the room 30 minutes before the experience</strong>. We’ll
          also help with all of the tidying up.
        </P>
        <Spacer />
        <H3>How many students can participate in the escape room, and how long does it take?</H3>
        <P>
          We’ve designed the escape room for a <strong>single class</strong> (approximately 30
          students) and to run for a <strong>single period</strong> (approximately 40-60 minutes).
          That said, we can tailor this to suit your needs.
        </P>
        <P>
          Please let us know, either in the form or by emailing us at{' '}
          <Link url="mailto:nswroadshow@stileeducation.com">nswroadshow@stileeducation.com</Link>,
          how many students will be participating and how long you have. We can then work out what’s
          manageable for the roadshow team.
        </P>
        <Spacer />
        <H3>How many Stile staff will be attending?</H3>
        <P>
          It depends on the week, but a minimum of three staff will be with the roadshow. There’s a
          maximum of six staff but some may be delivering experiences simultaneously at nearby
          schools.
        </P>
        <Spacer />
        <H3>
          Are any of the Stile staff in attendance teachers? Do they have Working With Children
          Checks?
        </H3>
        <P>
          There will always be one experienced science teacher on the Stile team to deliver
          experiences to your students. At least one teacher from your school must also be present
          for and actively engaged in the experience. Every Stile staff member in attendance will
          have a valid Working With Children Check.
        </P>
        <Spacer />
        <H3>Will you be taking any footage of these events?</H3>
        <P>
          We’ll send you a digital copy of our release forms to print for your students. They would
          need to take these forms home to be signed by their parent/guardian and returned to school
          to pass on to us.{' '}
          <strong>We will not take any photos of students who haven’t signed a release form</strong>
          . We will take footage at staff events to be used on socials, our website, and other
          channels. If you do not wish to have your photo or video of you taken, just let us know!
        </P>
        <Spacer />
        <H3>
          Where can I follow the Stile Sylla-<em>bus</em> tour?
        </H3>
        <P>
          We’ll be detailing our journey on our social pages and will send regular newsletters
          throughout the tour! If you want to be across the latest with Stile, follow us on our{' '}
          <Link openInNewTab url="https://www.facebook.com/groups/StileTeachers/">
            Stile Teachers Facebook group
          </Link>{' '}
          or any of our other social channels.
        </P>
        <Row justify="flex-start" space={theme.space.s}>
          {social.map((item) => (
            <Text
              key={item.text}
              size="l"
              as={Link}
              variant="hover"
              url={item.url}
              title={item.text}
            >
              <Icon iconId={item.iconId} ariaText={item.text} />
            </Text>
          ))}
        </Row>
        <Spacer />
        <H3>Who do I contact if I have further questions?</H3>
        <P>
          You can contact the Roadshow Team at{' '}
          <Link url="mailto:nswroadshow@stileeducation.com">nswroadshow@stileeducation.com</Link> or
          on <Link url={CONTACT.international.phone.url}>{CONTACT.international.phone.text}</Link>.
          For on-the-day only, contact Dimity, our Chief Roadshow Officer, on{' '}
          <Link url="tel:+61424573024">0424 573 024</Link>.
        </P>
      </TextBlock>

      <TextBlock title="The NSW Syllabus Implementation Guide" variant="dark">
        <P>
          The release of the science syllabus brings a period of changes, but it doesn’t need to be
          stressful. We’ve spent hundreds of hours analysing the changes, so you don’t have to.
        </P>
        <P>
          As a customisable core curriculum, Stile provides everything you need to deliver a
          high-quality, syllabus-aligned science program. Specifically designed to support the
          implementation of the new NSW Syllabus, download our comprehensive implementation guide
          below. It features our meticulously crafted scope and sequence document which acts as a
          blueprint for tailoring your school’s science curriculum while ensuring seamless alignment
          with the NSW syllabus. In true Stile form, this guide is fully customisable, empowering
          you to adapt it to your school’s unique schedule and preferences.
        </P>
        <StaticImage src="../../../stile-shared/assets/images/nsw/form.png" alt="" />
        <ButtonLink url="/resources/new-nsw-syllabus/" openInNewTab>
          Download
        </ButtonLink>
      </TextBlock>

      <TextBlock title="Follow us on the road">
        <P>
          We’ll be posting exciting information all about our journey through NSW, plus competitions
          with great prizes. If you want to be across the latest with Stile, follow us on our{' '}
          <Link openInNewTab url="https://www.facebook.com/groups/StileTeachers/">
            Stile Teachers Facebook group
          </Link>{' '}
          or any of our other social channels.
        </P>
        <Row justify="flex-start" space={theme.space.s}>
          {social.map((item) => (
            <Text
              key={item.text}
              size="l"
              as={Link}
              variant="hover"
              url={item.url}
              title={item.text}
            >
              <Icon iconId={item.iconId} ariaText={item.text} />
            </Text>
          ))}
        </Row>
      </TextBlock>

      <TextBlock title="Keen for more?">
        <P>
          <Link url="/resources/nsw/">Read up</Link> on how Stile will support your transition to
          the new NSW science syllabus.
        </P>
      </TextBlock>

      <NextPageBlock path="/resources/nsw/" />

      <CtaBlock />
    </PageLayout>
  );
}

export default ResourcesPage;

function RoadshowItem({
  title,
  locations,
  times,
  url,
}: {
  title: string;
  locations?: string;
  times: string;
  url?: string;
}) {
  const titleComponent = <P bold>{title}</P>;

  return (
    <Row
      justify="space-between"
      space={theme.space.xxs}
      style={{ borderBottom: `${theme.borders.xs} ${theme.colors.grey3}`, padding: '4px' }}
    >
      <Column space={theme.space.xxxs} style={{ maxWidth: '80%' }}>
        {url ? (
          <Link openInNewTab url={url}>
            {titleComponent}
          </Link>
        ) : (
          titleComponent
        )}

        <P size="s">{locations}</P>
        <P size="s">{times}</P>
      </Column>

      {url && (
        <ButtonLink openInNewTab url={url}>
          Book now
        </ButtonLink>
      )}
    </Row>
  );
}

function RoadshowMeetup({
  title,
  subtitle,
  link,
  date,
  location,
}: {
  title: string;
  subtitle: string;
  link: string;
  date: string;
  location: string;
}) {
  return (
    <Row
      justify="space-between"
      space={theme.space.xxs}
      style={{
        borderBottom: `${theme.borders.xs} ${theme.colors.grey3}`,
        padding: '4px',
        paddingBottom: 10,
        marginBottom: 40,
      }}
    >
      <Column space={theme.space.xxxs} style={{ maxWidth: '80%' }}>
        <div style={{ paddingBottom: 20 }}>
          <P bold size="l">
            {title}
          </P>
        </div>

        <Link openInNewTab url={link} style={{ color: theme.colors.green1, paddingBottom: 10 }}>
          <P>{subtitle}</P>
        </Link>
        <div style={{ paddingBottom: 10 }}>
          <P size="s">
            {date} · {location}
          </P>
        </div>
      </Column>
    </Row>
  );
}
